import { defineStore, Store } from "pinia";
import { DefaultGetParams, GetParams } from "@/interfaces/Types.definition";
import { getAllItemsAction, getItemAction } from "./common/actions";
import { useEventStore as eventStore } from "./EventStore";
import printJS from 'print-js';

import * as Sentry from "@sentry/browser";
import { Order, OrderInvoiceData, OrderStatusTypeEnum } from "@/interfaces/Order";
import { ITicket } from "@/interfaces/ITicket";
import { useEventStore } from "./EventStore";
import axios from "@/tools/Axios/axios";
import { toast } from "vue3-toastify";

export interface OrderState {
    order: Order | null,
    orders: Order[] | null,
    ticket: ITicket | null,
    tickets: ITicket[] | null,
    totalOrders: number,
    currentPage: number,
}

export const useOrderStore = defineStore("OrderStore", {
    state: (): OrderState => {
        return {
            order: null,
            orders: [],
            ticket: null,
            tickets: [],
            totalOrders: 0,
            currentPage: 1,
        }
    },
    actions: {
        async fetchOrder(orderId: string) {
            let params: GetParams = DefaultGetParams()
            params.populate = ['ticketOfficeSessionId', 'payments']
            await getItemAction<Order, Store<'OrderStore'>>(`/events/orders/${orderId}`, params, this, 'order')
        },
        async fetchAllOrders(sellingSession: string = null, isProduct: boolean = null, page: number = 1, limit: number = 50) {
            let params: GetParams = DefaultGetParams()
            params.filter = {
                status: [
                    OrderStatusTypeEnum.COMPLETED
                ],
                eventId: eventStore().currentEvent._id,
                origin: 'ticketOffice'
            }
            if (isProduct !== null) {
                params.filter['isProduct'] = isProduct
            }
            if (sellingSession) {
                params.filter['ticketOfficeSessionId'] = sellingSession
            }
            params.sort = [['createdAt', -1]]
            params.page = page
            params.limit = limit

            try {
                const { data, status } = await axios.get(`/events/orders/list`, { params })

                if (status === 204) {
                    this.orders = []
                    this.totalOrders = 0
                } else if (data) {
                    this.orders = data.data
                    this.totalOrders = data.total
                    this.currentPage = page
                }
            } catch (error) {
                Sentry.captureException(error)
                this.orders = []
                this.totalOrders = 0
            }
        },
        async fetchTicketsByOrder(order: Order) {
            const currentEvent = useEventStore().currentEvent
            let params: GetParams = DefaultGetParams()
            params.filter = {
                orderId: order._id,
                isProduct: order.isProduct,
                eventId: currentEvent._id
            }
            await getAllItemsAction<ITicket, Store<'OrderStore'>>(`/events/tickets/list`, params, this, 'tickets')
        },
        async sendTicketsToMail(order: Order, email:string) {
            const url = `/events/orders/send-tickets-from-mail/${order._id}`
            try {
                const { status } = await axios.post(url, { email })
                toast('Les billets de la commande ont bien été envoyés')
                return true
            } catch (error) {
                Sentry.captureException(error, { extra: {
                    apiRequest: JSON.stringify({email}),
                    apiResponse: JSON.stringify(error)
                }})
                toast.error("Une erreur est survenue")
                toast.error(error.message)
                return false
            }
        },
        async sendInvoice(order: Order, invoiceData: OrderInvoiceData): Promise<boolean> {
            const url = `/events/orders/send-invoice/${order._id}`
            try {
                const { status } = await axios.put(url, { invoiceData })
                toast('La facture a bien été envoyée')
                return true
            } catch (error) {
                Sentry.captureException(error, { extra: {
                    apiRequest: JSON.stringify(invoiceData),
                    apiResponse: JSON.stringify(error)
                }})
                toast.error("Une erreur est survenue")
                toast.error(error.message)
                return false
            }
        },
        getPdfOrderUrl(order: Order, tickets: ITicket[]= null) {
            const pdfTemplate = (eventStore().currentEvent.ticketing.hasBadge && !order.isProduct) ? 'badge' : 'ticket'
            let url = `${import.meta.env.VITE_API_BASE_URL}/events/orders/stream/orderIdTickets/${order._id}/${order.token}?pdfTemplate=${pdfTemplate}`
            if (order.isProduct && tickets.length) {
                const ticket = tickets[0]
                url = `${import.meta.env.VITE_API_BASE_URL}/events/orders/stream/ticketId/${ticket._id}/${order.token}`
            }
            return url
        },
        getPdfTicketUrl(order: Order, ticket: ITicket) {
            const pdfTemplate = (eventStore().currentEvent.ticketing.hasBadge) ? 'badge' : 'ticket'
            return `${import.meta.env.VITE_API_BASE_URL}/events/orders/stream/ticketId/${ticket._id}/${order.token}?pdfTemplate=${pdfTemplate}`
        },
        printOrder(order: Order, tickets: ITicket[]= null) {
            const printable = this.getPdfOrderUrl(order, tickets)
            printJS({
                printable,
                showModal: true,
                modalMessage: 'Chargement du document...'
            })
        },
        printTicket(order: Order, ticket: ITicket) {
            const printable = this.getPdfTicketUrl(order, ticket)
            printJS({
                printable,
                showModal: true,
                modalMessage: 'Chargement du billet...'
            })
        },
    }
})